<script>
    import { onMount } from "svelte";

    import {
        DecimalInput,
        Loader,
        NotificationModal,
        CustomTooltip
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        BillingType,
        DirectInsuranceType,
        DirectUserRoles,
    } from "../../../Utils/utilEnums.svelte";

    import {
        getMinHourlyRate,
        getMinScheduleFeeBasedOnRule,
        getMinTotalFee,
        patientPaysTotalFeeBasedOnCalculation,
        getNotExceedingGapChargeValue,
        billingRuleCalculateTotals,
        getInitTotalFee,
        getCalculatedUnitValue,
        getCalculatedHourlyRate,
        isWcOrTacOrDefOrCusOrOverAMARates,
    } from "../helpers/billingRuleHelper.svelte";

    import {
        getAmountInCurrency,
        getTodayWithTime,
        isDateInPast,
        isObjectEmpty,
        showError,
        showException,
        showInfo,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        billingRuleUnitValueAndNoGapProvider,
        enablePatientClaimForEncounter,
        getMBSRebateUnits,
        syncBillingStatus,
    } from "../helpers/encounterHelper.svelte";

    import { cacheInsuranceFunds } from "../../../Utils/utilCache.svelte";
    import { insuranceTypesMayAllowAmaBillingRule } from "../../../Utils/utilConstants.svelte";

    import { amaUnitValue, noPatientClaimFunds, user, insuranceFund } from "../../../stores";
    import { addErrorInput, removeErrorInput } from "../helpers/encounterUtils.svelte";
    import InvoiceReasonDetails from "./InvoiceReasonDetails.svelte";
    import Button from "../../../Components/Common/Button.svelte";
    import InfoIcon from "../../../Components/Common/InfoIcon.svelte";

    export let isAddBillingRuleModalOpen = false,
        isAsaItems = false,
        latestObjDetails = {},
        patientEncounterDetails = {},
        itemsData = [],
        paymentsData = [],
        isFromQuoteOnlyEncounter = false,
        closeModal = () => {},
        saveBillingRule = () => {},
        doctorBillingRules = [];

    $: isFromQuote = latestObjDetails.isLatestQuoteData;

    $: isEncounterInThePast = isDateInPast(
        patientEncounterDetails.scheduledDate,
        getTodayWithTime(),
    );

    $: privateWithInsuranceRebatesZero = fundResults && fundResults.sumInsuranceRebates == 0 && latestObjDetails.insuranceType == 0;

    $: hasTimeItem = itemsData.some(({ infoType }) => infoType == "Time");

    $: encounterTypeAnaesthesia = patientEncounterDetails.typeOfBill == 0;

    $: displayItems = _getDisplayItems(itemsData, isAsaItems);

    $: displayBillingTypes = _getDisplayBillingTypes(
        isWcOrTacUsingAmaRates,
        latestObjDetails.insuranceType,
        billingObj.patientInsuranceId,
        hasTimeItem,
        isAsaItems,
        patientEncounterDetails.doctorProvider
    );

    $: showPatientPaysSelector = _getShowPatientPaysSelector(
        latestObjDetails,
        fundsResults,
        patientEncounterDetails.isNoGapProgram
    );

    $: showPatientClaimToggle = enablePatientClaimForEncounter(
        patientEncounterDetails,
    );

    $: showChargePatientSelector = _getShowChargePatientSelector(
        isAsaItems,
        isFromQuote,
        fundsResults,
        isFromQuoteOnlyEncounter,
        latestObjDetails,
        isEncounterInThePast,
        patientEncounterDetails.billingData,
    );

    $: isSaveBtnDisabled = _getSaveBtnDisabled(billingObj, isGapFeeInvalid);

    $: _getDisplayBillingObjTitle = (billingObjType) => {
        if (isWcOrTacUsingAmaRates && billingObjType == 2)
            return "Set Unit Value charge";

        if (Number.isInteger(billingObjType)) {
            const type = BillingType.find((a) => a.id === billingObj.type);
            return type?.type ? `${type.type} charge` : "";
        }

        return "";
    };

    $: isWcOrTacUsingAmaRates =
        insuranceTypesMayAllowAmaBillingRule.includes(
            latestObjDetails.insuranceType,
        ) && latestObjDetails.usingAmaRates;

    $: btnOptions = [
        /* {
            text: isUpdatingBillingRule ? "Saving" : "Save",
            class: `btn btn-primary ${isUpdatingBillingRule ? "cursor-not-allowed" : ""}`,
            isConfirming: isUpdatingBillingRule,
            loading: isSaveBtnDisabled || isUpdatingBillingRule,
            function: async () => await handleSaveBillingRule(),
        }, */
    ];

    $: innerHeight = 0;

    $: if (contentRef && innerHeight) {
        hasOverflow =
            contentRef && contentRef.scrollHeight > contentRef.clientHeight;
    }

    // auto select TOTAL FEE if No Gap Fee is disabled
    $: if (isGapFeeBtnDisabled && !isTotalFeeBtnDisabled) {
        billingObj.patientPaysTotalFee = true;
        getInvoiceReasonDetails();
    }

    // auto select GAP FEE if Total Fee is disabled
    $: if (!isGapFeeBtnDisabled && isTotalFeeBtnDisabled) {
        billingObj.patientPaysTotalFee = false;
        getInvoiceReasonDetails();
    }

    $: isEncounterInNoGapProgram = patientEncounterDetails.isNoGapProgram;

    $: if (isEncounterInNoGapProgram && fundData)
        handleEncounterInNoGapGroup(
            filteredDoctorBillingRules,
            displayBillingTypes,
        );

    $: isInvoiceAndHasQuote = !isFromQuote && patientEncounterDetails.estimates.some(p => p.status != 9);

    let isCalculatingTotals = false,
        isUpdatingBillingRule = false;

    let amountFocused = false;

    let billingObj = {},
        amountSelectorProperties = {
            min: 0,
            max: 0,
            amountSelector: 0,
            divisionsDivider: 0,
        },
        dynamicMin = 0, // min value of range limit the max with decimals and step
        dynamicStep = 1, // step value of range sometimes need to change dynamically
        fundsResults = {};

    let fundData,
        fundResults,
        amountSelectorDivisions = 0;

    let unitValue = 0,
        hourlyRate = 0;

    // show error notification and disable submit btn if billing type is Set Gap Fee and selected gap is less than patient already paid amount
    let isGapFeeInvalid = false,
        amountPatientAlreadyPays = 0;

    let isGapFeeBtnDisabled = false,
        isTotalFeeBtnDisabled = false,
        disableText = "",
        patientClaimDisabledText = "Patient Claim cannot be turned on if Patient Pays Gap Fee";

    let hideExpectedInsuranceRebateWarningText = true;
    let displayExpectedInsuranceRebateWarningText = null;

    let contentRef, hasOverflow;

    let billingRuleUnitValueAndNoGapProviderWarningModalOpen = false;

    let showUnitValueRuleWithZeroGapModal = false;

    let filteredDoctorBillingRules = [],
        selectedBillingRuleId;

    let confirmSavePrivateTotalWithinFundRules = false,
        showConfirmSavePrivateTotalWithinFundRulesModal = false;
    
    let patientClaim;
    let insuranceInfoDetails;

    onMount(async () => {
        //set patient claim to what's already selected on billingData or on encounter
        patientClaim = latestObjDetails.patientClaim ?? patientEncounterDetails.patientClaim;


        await apiMethods.getMethod
            .getPatientEncounterFundData(latestObjDetails.id)
            .then((res) => (fundData = res));

       
        _initSelectedBillingRule();

        if (latestObjDetails.withinFundRules) {
            // if latest billing data is within fund rules, set charge patient to "Before" by default
            latestObjDetails.newOopType = latestObjDetails.oopType
                ? latestObjDetails.oopType
                : 0;

            // if insurance type is either private, self-insured or DVA, set patientPaysTotalFee to FALSE by default
            const defaultPatientPaysTotalFeeInsuranceTypes = [0, 11, 20, 10];
            billingObj.patientPaysTotalFee =
                latestObjDetails.patientPaysTotalFee
                    ? latestObjDetails.patientPaysTotalFee
                    : !defaultPatientPaysTotalFeeInsuranceTypes.includes(
                          latestObjDetails.insuranceType,
                      );
        } else {
            billingObj.patientPaysTotalFee =
                latestObjDetails.patientPaysTotalFee;
            // latestObjDetails.newOopType = isFromQuoteOnlyEncounter
            //     ? 0
            //     : (latestObjDetails.oopType ?? Number(0));
            latestObjDetails.newOopType = latestObjDetails.oopType ?? Number(0);
        }

        unitValue = getCalculatedUnitValue(fundData.items, latestObjDetails, {
            totalFee: latestObjDetails.totalFee,
        });

        hourlyRate = getCalculatedHourlyRate(
            fundData.items,
            latestObjDetails,
            {
                totalFee: latestObjDetails.totalFee,
            },
            isFromQuote,
        );

        await initDoctorBillingRuleOptions();
        alignInvoiceCalculationAmountBasedOnPayments();
        if(billingObj.type){
            await calculateTotals(false, true); //calculate totals when modal is opened to update totals correctly
        }

        //get insurance info details for tooltip on private encounters
        insuranceInfoDetails = await getInsuranceInfo();

    });

    const alignInvoiceCalculationAmountBasedOnPayments = () => {
        //check if quote was prepay and encounter has estimates with payments
        if(!isFromQuote){
            var quoteData = patientEncounterDetails.billingData.find((b) => b.isLatestQuoteData);
            if(quoteData && quoteData.oopType == 0 && patientEncounterDetails.estimates.length > 0){
                //quote exist, get estimate
                var lastEstimate = patientEncounterDetails.estimates[patientEncounterDetails.estimates.length - 1];
                if(lastEstimate && lastEstimate.payments.length > 0 && lastEstimate.payments.filter((p) => p.paidBy == 0)){
                    let receivedPaymentsSum = lastEstimate.payments
                        .filter(p => p.paidBy === 0 && (p.status === 1 || p.status === 2))
                        .map(e => e.amount)
                        .reduce((sum, val) => sum + val, 0);
                    var fullyPaid = receivedPaymentsSum == lastEstimate.totalFee;
                    if(fullyPaid){
                        //check for refunds
                        let refundedPaymentsSum = (lastEstimate.payments ?? [])
                            .filter(p => p.paidBy === 0 && p.status === 5)
                            .map(e => e.amount)
                            .reduce((sum, val) => sum + val, 0);
                        if(refundedPaymentsSum > 0){
                            if(quoteData.patientPaysTotalFee){
                                //total fee
                                var newAmount = quoteData.totalFee - refundedPaymentsSum;
                                if (newAmount != billingObj.amount) {
                                    billingObj.amount = newAmount;
                                    showInfo("Invoice total fee was updated to reflect the refunds");
                                }
                            } 
                            else 
                            {   
                                //gap fee
                                var newAmount = quoteData.actualGap - refundedPaymentsSum;
                                if (newAmount != billingObj.amount) {
                                    billingObj.amount = newAmount;
                                    showInfo("Invoice gap fee was updated to reflect the refunds");
                                }
                            }
                        }
                    }

                }
            }
        }
    }

    const handleEncounterInNoGapGroup = (templates, billingRules) => {
        // show SET GAP FEE templates ONLY
        filteredDoctorBillingRules = templates.filter((t) => t.type == 3);

        // reset selected template if not in filtered templates
        if (
            !filteredDoctorBillingRules.some(
                (t) => t.id == selectedBillingRuleId,
            )
        )
            selectedBillingRuleId = "";

        // show SET GAP FEE billing rules ONLY
        displayBillingTypes = billingRules.filter((b) => b.id == 3);

        // FORCE gap fee to be 0 and billing rule to be SET GAP FEE
        billingObj.amount = 0;
        billingObj.type = 3;

        // set amount selector property
        handleAmountSelectorProperty(false);
    };

    const sortRulesList = (rulesList) => {
        const priorityLabels = ['No Gap', 'AMA rates'];

        return [...rulesList].sort((a, b) => {
            const aPriority = priorityLabels.indexOf(a.name);
            const bPriority = priorityLabels.indexOf(b.name);

            // both are priority items
            if (aPriority !== -1 && bPriority !== -1) {
                return aPriority - bPriority;
            }

            // only a is priority
            if (aPriority !== -1) return -1;

            // only b is priority
            if (bPriority !== -1) return 1;

            // normal alphabetical order (case-insensitive)
            return a.name.localeCompare(b.name, undefined, {
                sensitivity: 'base',
            });
        });
    }

    const initDoctorBillingRuleOptions = async () => {
        const { insuranceType, useAsaCodes, mbsData } =
            latestObjDetails;
        
        const { type: billingType } = billingObj;

        if (
            useAsaCodes ||
            isWcOrTacOrDefOrCusOrOverAMARates(latestObjDetails)
        ) {
            filteredDoctorBillingRules = [
                {
                    type: 2,
                    id: -2,
                    name: "AMA rates",
                    oopType: 0,
                    amount: [
                        DirectInsuranceType.WorkersCompensation,
                        DirectInsuranceType.TrafficAccident,
                    ].includes(latestObjDetails.insuranceType)
                        ? $amaUnitValue
                        : $amaUnitValue //_getMaxUnitValue(),
                },
            ];

            if(latestObjDetails.insuranceType == DirectInsuranceType.Overseascover ||
                latestObjDetails.insuranceType == DirectInsuranceType.Cosmetic) {
                    //add also doctor rules of type unitvalue/gapfee/totalfee
                    filteredDoctorBillingRules = [
                        ...filteredDoctorBillingRules,
                        ...doctorBillingRules.filter(
                            (rule) => rule.type == 2 || rule.type == 5
                        )
                    ]
                }
            //todo: sort filteredDoctorBillingRules by name, but keep default ones at the top
            filteredDoctorBillingRules = [...sortRulesList(filteredDoctorBillingRules)];
            

            // Pre-select billing rule if no rules selected
            if (!billingType)
                await handleSelectBillingRule({
                    target: { value: Number(-2) },
                });

            return;
        }

        if (!!doctorBillingRules?.length) {
            const defaultBillingRules = [
                {
                    type: 3,
                    id: -1,
                    name: "No Gap",
                    oopType: 0,
                    amount: 0,
                },
                {
                    type: 2,
                    id: -2,
                    name: "AMA rates",
                    oopType: 0,
                    amount: [
                        DirectInsuranceType.WorkersCompensation,
                        DirectInsuranceType.TrafficAccident,
                    ].includes(latestObjDetails.insuranceType)
                        ? $amaUnitValue
                        : $amaUnitValue //_getMaxUnitValue(),
                },
            ];

            filteredDoctorBillingRules = [
                ...defaultBillingRules,
                ...doctorBillingRules,
            ];

            if (
                !useAsaCodes &&
                !mbsData.some((item) => item.infoType == "Time")
            )
                filteredDoctorBillingRules = filteredDoctorBillingRules.filter(
                    (rule) => rule.type != 4,
                );

            if (insuranceType == DirectInsuranceType.PublicInPrivate) {
                const restrictedBillingTypes = [2, 4, 5, 6];

                filteredDoctorBillingRules = filteredDoctorBillingRules.filter(
                    (rule) => restrictedBillingTypes.includes(rule.type),
                );
            }

            const defaultBillingRule = filteredDoctorBillingRules.find(
                (br) => br.isDefault,
            );

            //todo: sort filteredDoctorBillingRules by name, but keep default ones at the top
            filteredDoctorBillingRules = [...sortRulesList(filteredDoctorBillingRules)];

            // Pre-select default billing rule if no rules selected
            if (defaultBillingRule?.id && !billingType)
                handleSelectBillingRule({
                    target: { value: defaultBillingRule.id },
                });
        }
    };

    const handleSaveBillingRule = async () => {
        await saveBillingRule();
    }

    let invoiceReasonDetails = null;

    const getInvoiceReasonDetails = async () => {
        invoiceReasonDetails = null;
        if(isFromQuote) return;

        let quoteData = patientEncounterDetails.billingData.find((b) => b.isLatestQuoteData);
        if(!quoteData || quoteData.oopType == 0) {
            const request = {
                patientEncounterId: patientEncounterDetails.id,
                doctorId: patientEncounterDetails.doctorId,
                invoiceBillingDataId: latestObjDetails.id,
                QuoteBillingDataId: quoteData?.id ?? 0,
                PatientPaysTotalFee: billingObj.patientPaysTotalFee,
                InvoiceTotal: billingObj.totalFee,
                InvoiceGap: billingObj.gap,
                AddGST: latestObjDetails.addGST,
            };

            await apiMethods.postMethod
                .getInvoiceReason(request)
                .then((res) => {
                    if (res && res.invoiceReason != 1) {
                        invoiceReasonDetails = res;
                        return;
                    }
                })
                .catch((err) => {
                    invoiceReasonDetails = null;
                });
        }
      
    }

    export const updateBillingRule = async (confirmUpdate = false) => {
        /*
            Check below before saving billing rule if calculated result returns gap 0 with rule Set Unit Value
            1. check if encounter allows Set Gap Fee
            2. check if final gap is 0 and selected rule is NOT "Set Gap Fee"
                - if NO, continue saving
                - if YES, show warning and ask for confirmation
            3. Confirm action - set rule to "Set Gap Fee" and continue saving
        */
        if (
            billingObj &&
            latestObjDetails.insuranceType == 0 &&
            billingObj.gap == 0 &&
            billingObj.type != 3
        )
            _checkZeroGapWithBillingRule();
        else {
            const selectedBillingRuleUnitValueAndNoGapProvider =
                billingRuleUnitValueAndNoGapProvider(
                    { ...latestObjDetails, billingType: billingObj.type },
                    patientEncounterDetails.doctorProvider,
                    fundData,
                );

            if (selectedBillingRuleUnitValueAndNoGapProvider && !confirmUpdate)
                _openBillingRuleUnitValueAndNoGapProviderModal();
            else {
                if (
                    _privateTotalFeeWithinFundRules() &&
                    !confirmSavePrivateTotalWithinFundRules
                ) {
                    _openConfirmSavePrivateTotalWithinFundRulesModal();
                    return;
                }

                isUpdatingBillingRule = true;

                await updateTotalFee();

                confirmSavePrivateTotalWithinFundRules = false;

                // set patientPaysTotalFee property to TRUE and update encounter
                if (confirmUpdate)
                    patientEncounterDetails.billingData =
                        patientEncounterDetails.billingData.map((bd) => {
                            if (bd.id == latestObjDetails.id)
                                return {
                                    ...bd,
                                    patientPaysTotalFee: true,
                                };
                            return bd;
                        });

                if (!isFromQuote)
                    patientEncounterDetails = await syncBillingStatus(
                        patientEncounterDetails,
                    );

                isUpdatingBillingRule = false;

                closeModal(patientEncounterDetails);
            }
        }
    };

    const updateUnitValueRuleToSetGapFee = async () => {
        billingObj.type = 3;
        billingObj.amount = 0;

        await calculateTotals();

        _closeUnitValueRuleWithZeroGapModal();

        updateBillingRule(true);
    };

    const calculateTotals = async (isFromSlider = false, isInitialLoad = false) => {
        if (isCalculatingTotals) return;

        isCalculatingTotals = true;

        if (
            billingObj.type == 3 &&
            billingObj.amount <= amountPatientAlreadyPays &&
            !(billingObj.amount === 0 && amountPatientAlreadyPays === 0)
        )
            isGapFeeInvalid = true;
        else isGapFeeInvalid = false;

        // res here has new property "usingAmaRates" which need to be saved in latest object
        let res = await billingRuleCalculateTotals(
            patientEncounterDetails.doctorId,
            patientEncounterDetails.id,
            latestObjDetails.id,
            billingObj,
        );


        // when re-calculate with saved value, might cause data scaling
        if (res.error) showError(res.error);
        else if (res.gap < -0.1) {
            showError(
                "Invalid gap amount, please refresh the page and try again and contact support if the issue persists.",
                undefined,
                true,
            );
            isCalculatingTotals = false;
            return;
        } else {
            fundResults = res.resolve; //set fund results obj

            billingObj.expectedInsuranceRebate = res.resolve.insurance;
            billingObj.gap = res.resolve.gap >= 0 ? res.resolve.gap : 0;
            billingObj.totalFee = res.resolve.totalFee;
            fundsResults = {
                ...res.resolve,
                withinFundRules: res.resolve.whitinFundRules,
            };
            // update new property "usingAmaRates" to billingObj
            billingObj.usingAmaRates = res.resolve.usingAmaRates;
        }

        //if not is initial load and not invoice with quote or invoice with quote but selfinsured, then recalculate patientpaystotalfee
        //&& (!isInvoiceAndHasQuote || (isInvoiceAndHasQuote && latestObjDetails.insuranceType == 11))
        if(!isInitialLoad ){
            var patientPaysResponse = await patientPaysTotalFeeBasedOnCalculation(
                isFromSlider,
                latestObjDetails.insuranceType,
                fundsResults,
                billingObj,
                patientEncounterDetails
            );

            if(patientPaysResponse != null) { //if null, do not change anything
                const {
                    patientPaysTotalFee,
                    gapFeeBtnDisabled,
                    totalFeeBtnDisabled,
                    disableText: resDisableText,
                } = patientPaysResponse;
                
                billingObj.patientPaysTotalFee = patientPaysTotalFee;
                if(patientPaysTotalFee){
                    //set patientclaim to true
                    patientClaim = true;
                }

                isGapFeeBtnDisabled = gapFeeBtnDisabled;

                isTotalFeeBtnDisabled = totalFeeBtnDisabled;

                disableText = resDisableText;
            }
            
        }
        

        hideExpectedInsuranceRebateWarningText =
            fundsResults.withinFundRules ||
            (!fundsResults.withinFundRules &&
                latestObjDetails.patientInsurance == "Basic Plus");
        
        displayExpectedInsuranceRebateWarningText =
            _getInsuranceRebateWarningText(
                latestObjDetails,
                patientEncounterDetails,
                fundData,
            );

        unitValue = getCalculatedUnitValue(
            fundData.items,
            latestObjDetails,
            fundsResults,
        );

        hourlyRate = getCalculatedHourlyRate(
            fundData.items,
            latestObjDetails,
            fundsResults,
            isFromQuote,
        );

        // if billing type is 3 (set Gap Fee) and amount == 0, set patientPaysTotalFee to FALSE
        if (billingObj.type == 3 && billingObj.amount == 0)
            billingObj.patientPaysTotalFee = false;

        isCalculatingTotals = false;

        await getInvoiceReasonDetails();
    };

    const updateTotalFee = async () => {
        const data = {
            ...latestObjDetails,
            billingType: billingObj.type,
            patientPaysTotalFee: billingObj.patientPaysTotalFee,
            withinFundRules: isObjectEmpty(fundsResults)
                ? latestObjDetails.withinFundRules
                : fundsResults.withinFundRules,
            // oopType: isFromQuoteOnlyEncounter
            //     ? 0
            //     : latestObjDetails.hasOwnProperty("newOopType")
            //       ? latestObjDetails.newOopType
            //       : (latestObjDetails.oopType ?? Number(0)),
            oopType: latestObjDetails.hasOwnProperty("newOopType")
                ? latestObjDetails.newOopType
                : (latestObjDetails.oopType ?? Number(0)),
            calculationAmount:
                parseFloat(billingObj.amount.toFixed(2)) ?? Number(0), // unit value used to calculate fees
            actualGap: billingObj.gap,
            totalFee: billingObj.totalFee,
            rebate: billingObj.expectedInsuranceRebate,
            usingAmaRates: billingObj.usingAmaRates, // new property "usingAmaRates"
        };

        // set patientPaysTotalFee FALSE when latest obj insurance type is Self-insured and gap fee charged 0
        // otherwise set patientPaysTotalFee TRUE if latest obj insurance type is Self-insured but gap fee is greater than 0
        if (data.insuranceType == 11 && data.billingType == 3)
            data.patientPaysTotalFee = data.calculationAmount > 0;

        const { insuranceType, usingAmaRates, asaTimeData, calculationAmount } =
            data;

        // // In these cases asaTimeData is using MBS time code as reference, when update billing rules, update asaTimeData accordingly
        // if (
        //     ((insuranceTypesMayAllowAmaBillingRule.includes(insuranceType) &&
        //         usingAmaRates) ||
        //         insuranceType == DirectInsuranceType.ThirdPartyInsurer) &&
        //     asaTimeData?.length
        // )
        //     data.asaTimeData = data.asaTimeData.map((td) => {
        //         td.feeCharged = td.units * calculationAmount;
        //         return td;
        //     });

        // If has asaTimeData which means it uses MBS time code as reference, when update billing rule, update asaTimeData accordingly
        if (asaTimeData?.length) {
            if (data.billingType == 2) {
                // SetUnitValue - clean multiplication
                data.asaTimeData = data.asaTimeData.map((td) => {
                    td.feeCharged = td.units * calculationAmount;
                    return td;
                });
            } else {
                // SetTotalFee - remainder approach to avoid rounding errors
                const itemsFeeSum = data.asaData?.reduce((sum, item) => sum + (item.feeCharged || 0), 0) || 0;
                const roundedItemsFeeSum = Math.round(itemsFeeSum * 100) / 100;
                data.asaTimeData = data.asaTimeData.map((td) => {
                    td.feeCharged = data.totalFee - roundedItemsFeeSum;
                    return td;
                });
            }
        }

        patientEncounterDetails.billingData =
            patientEncounterDetails.billingData.map((bd) => {
                if (bd.id == data.id) return { ...bd, ...data };
                return bd;
            });
        // if scheduled date is in the past, prevent oopType change from Post Pay to Pre Pay
        // whenever oopType changes, all latest billing data oopType should be synced
        if (data.newOopType == 0 && !isEncounterInThePast) {
            patientEncounterDetails.billingData =
                patientEncounterDetails.billingData.map((bd) => {
                    if (bd.id == data.id) return { ...bd, oopType: Number(0) };
                    return bd;
                });
        } else if (data.newOopType == 1)
            patientEncounterDetails.billingData =
                patientEncounterDetails.billingData.map((bd) => {
                    if (bd.id == data.id) return { ...bd, oopType: Number(1) };
                    return bd;
                });

        //update also item rebates && fee charges
        var billingData = patientEncounterDetails.billingData.find(
            (bd) => bd.id == data.id,
        );

        const { itemsFees } = fundsResults;

        if (isAsaItems) {
            if (itemsFees)
                billingData.asaData = billingData.asaData.map((asa) => {
                    const itemFee = itemsFees.find(
                        (fee) =>
                            fee.itemIdentifier.toLowerCase() ==
                                asa.itemCode.toLowerCase() &&
                            fee.subItemIdentifier.toLowerCase() ==
                                asa.subItemCode.toLowerCase(),
                    );

                    return itemFee
                        ? {
                              ...asa,
                              feeCharged: itemFee.feeCharged,
                              rebate: itemFee.rebate,
                              fieldChange: itemFee.fieldChange
                          }
                        : asa;
                });
        } else {
            if (itemsFees)
                billingData.mbsData = billingData.mbsData.map((mbs) => {
                    const itemFee = itemsFees.find(
                        (fee) =>
                            fee.itemIdentifier == mbs.itemNum.toString() &&
                            fee.subItemIdentifier == mbs.subItemNum.toString(),
                    );

                    return itemFee
                        ? {
                              ...mbs,
                              feeCharged: itemFee.feeCharged,
                              rebate: itemFee.rebate,
                              fieldChange: itemFee.fieldChange,
                              basicUnits: itemFee.units
                          }
                        : mbs;
                });
        }

        if(billingObj.patientPaysTotalFee == false)
        { //make sure that if patient pays gap fee, patient claim is set to false
            patientClaim = false;
        }

        //set encounter patient claim with what was selected on rule patient claim
        patientEncounterDetails.patientClaim = patientClaim;
        //set also billing data patient claim value to what was selected
        billingData.patientClaim = patientClaim;

        await apiMethods.putMethod
            .updatePatientEncounter(patientEncounterDetails, true, "Save rule - Update total fee")
            .then((res) => (patientEncounterDetails = { ...res }))
            .catch((err) => showException(err));
    };

    const handleSelectBillingRuleUnitValueAndNoGapProvider = async () =>
        await updateBillingRule(true);

    const handleSelectBillingRule = async (e) => {
        selectedBillingRuleId = Number(e.target.value);

        if (!selectedBillingRuleId) return;

        const selectedBillingRule = filteredDoctorBillingRules.find(
            (rule) => rule.id == selectedBillingRuleId,
        );

        if (
            !displayBillingTypes.some(
                (type) => type.id == selectedBillingRule.type,
            )
        )
            return;

        const { type, amount, oopType } = selectedBillingRule;

        billingObj.type = type;
        billingObj.newOopType = oopType;

        handleAmountSelectorProperty(false);

        const { min, max } = amountSelectorProperties;

        if (amount < min) {
            billingObj.amount = min;
            showInfo(`Minimum allowed value is ${getAmountInCurrency(min)}`);
        } else billingObj.amount = amount;

        await calculateTotals();
    };

    const handleAmountSelectorProperty = async (needApiCalculation = true) => {
        if(billingObj.type){
            removeErrorInput("billingTypeSelector", [])
        }
        switch (billingObj.type) {
            case 2:
                const isCosmeticWithAsaItems =
                    latestObjDetails.insuranceType == 15 && isAsaItems;

                const isThirdPartyInsurance =
                    latestObjDetails.insuranceType == 18;

                let minValue = _getMinUnitValue(fundData.items);

                if (isCosmeticWithAsaItems) minValue = Math.min(minValue, 20);

                dynamicMin = amountSelectorProperties.min = minValue;
                amountSelectorProperties.max = _getMaxUnitValue();
                amountSelectorProperties.amountSelector = Math.min(
                    minValue,
                    40,
                );
                dynamicStep = amountSelectorProperties.divisionsDivider = 1;
                break;
            case 3:
                dynamicMin = amountSelectorProperties.min = 0;
                amountSelectorProperties.max = 2000;
                // amountSelectorProperties.amountSelector =
                //     billingObj.id == 1 ? 0 : 250;
                amountSelectorProperties.amountSelector = 0;
                dynamicStep = amountSelectorProperties.divisionsDivider = 10;

                if (paymentsData && paymentsData.length)
                    _getAmountPatientAlreadyPays();

                break;
            case 4:
                dynamicMin = amountSelectorProperties.min = getMinHourlyRate(
                    fundData,
                    isFromQuote,
                    latestObjDetails,
                );
                amountSelectorProperties.max = 1500;
                amountSelectorProperties.amountSelector =
                    amountSelectorProperties.min;
                dynamicStep = amountSelectorProperties.divisionsDivider = 10;
                break;
            case 5:
                const isBillingDataHasNoBillingRule =
                    !latestObjDetails.hasOwnProperty("billingType") ||
                    !latestObjDetails.billingType;

                const minTotalFee = getMinTotalFee(fundData, latestObjDetails.billingCodesType == 1, latestObjDetails.insuranceType);
                const initTotalFee = getInitTotalFee(fundData);

                dynamicMin = amountSelectorProperties.min = minTotalFee;
                amountSelectorProperties.max = 3000;
                amountSelectorProperties.amountSelector =
                    isBillingDataHasNoBillingRule
                        ? initTotalFee
                        : minTotalFee > 600
                          ? minTotalFee
                          : 600;
                dynamicStep = amountSelectorProperties.divisionsDivider = 10;

                break;
            case 6:
            case 7:
            case 8:
            case 9:
            case 10:
            case 11:
            case 12: {
                const minScheduleFeeToGapZero = getMinScheduleFeeBasedOnRule(billingObj.type, fundData);
       
                dynamicMin = amountSelectorProperties.min = minScheduleFeeToGapZero > 100 ? minScheduleFeeToGapZero : 100;
                amountSelectorProperties.max = 300;
                amountSelectorProperties.amountSelector = minScheduleFeeToGapZero > 100 ? minScheduleFeeToGapZero : 100;
                dynamicStep = amountSelectorProperties.divisionsDivider = 1;
                break;
            }
               
            case 13: { 
                const minScheduleFeeToGapZero = getMinScheduleFeeBasedOnRule(billingObj.type, fundData);
       
                dynamicMin = amountSelectorProperties.min = minScheduleFeeToGapZero > 40 ? minScheduleFeeToGapZero : 40;
                amountSelectorProperties.max = 200;
                amountSelectorProperties.amountSelector = 100;
                dynamicStep = amountSelectorProperties.divisionsDivider = 1;
            }
               
            default:
                break;
        }

        const maxMinDiff =
            amountSelectorProperties.max - amountSelectorProperties.min;
        const divisions =
            (maxMinDiff > 0 ? maxMinDiff : 1) /
            amountSelectorProperties.divisionsDivider;

        amountSelectorDivisions = parseInt(divisions);

        billingObj.amount = amountSelectorProperties.amountSelector;

        // FORCE gap fee to be 0 if encounter is in NO-GAP Program
        if (billingObj.type == 3)
            billingObj.amount = isEncounterInNoGapProgram
                ? 0
                : amountSelectorProperties.amountSelector;

        if (needApiCalculation) {
            await calculateTotals();

            if (billingObj.type != 3) return;

            // auto select patient pays option for Set Gap Fee rule
            showPatientPaysSelector && handlePatientPaysTotal();
        }
    };

    const handleSelectPrev = () => {
        if (!amountSelectorProperties.divisionsDivider) return;

        if (billingObj.amount && billingObj.amount > 0) {
            const nextValue =
                Math.floor(billingObj.amount) -
                amountSelectorProperties.divisionsDivider;

            const prevAmount = billingObj.amount;

            billingObj.amount = Math.max(
                nextValue,
                amountSelectorProperties.min,
            );

            const amountChanged = prevAmount != billingObj.amount;

            if (amountChanged) calculateTotals();
        }
    };

    const handleSelectNext = () => {
        if (!amountSelectorProperties.divisionsDivider) return;

        if (!billingObj.amount) billingObj.amount = 0;

        const nextValue =
            Math.floor(billingObj.amount) +
            amountSelectorProperties.divisionsDivider;

        const prevAmount = billingObj.amount;

        billingObj.amount = Math.min(nextValue, amountSelectorProperties.max);

        const amountChanged = prevAmount != billingObj.amount;

        if (amountChanged) calculateTotals();
    };

    const handlePatientPaysTotal = (isTotalFee = false) => {
        if (!isTotalFee && isGapFeeBtnDisabled) return;

        if (isTotalFee && isTotalFeeBtnDisabled) return;

        if (billingObj.patientPaysTotalFee == isTotalFee) return;

        const { insuranceType } = latestObjDetails;

        // Private
        if (
            (insuranceType == 0 || insuranceType == 10) &&
            !fundsResults.withinFundRules &&
            billingObj.patientPaysTotalFee &&
            !isTotalFee
        ) {
            billingObj.patientPaysTotalFee = isTotalFee;
            billingObj.amount = 
                getNotExceedingGapChargeValue(
                    fundData,
                    fundsResults,
                    billingObj,
                    amountSelectorProperties,
                );
            calculateTotals();
            return;
        }

        // Self-insured
        if (insuranceType == 11) {
            if (!isObjectEmpty(fundsResults) && fundsResults.gap > 0) {
                // triggered when calculate amount
                billingObj.patientPaysTotalFee = true;
                return;
            } else if (billingObj.type == 3 && billingObj.amount > 0) {
                // default data when open modal
                billingObj.patientPaysTotalFee = true;
                return;
            }
        }

        // Overseas without fund, Cosmetic
        if ((insuranceType == 10 && billingObj.patientInsuranceId == 0) || insuranceType == 15) {
            billingObj.patientPaysTotalFee = true;
            return;
        }

        // If total fee was selected, set patient claim to true
        if(isTotalFee) {
            patientClaim = true;
        } else {
            patientClaim = false;
        }

        billingObj.patientPaysTotalFee = isTotalFee;

        getInvoiceReasonDetails();
    };

    // HELPER FUNCTIONS
    const _getInsuranceRebateWarningText = (latestObj, encounter, fundData) => {
        const { insuranceType } = latestObj;
        const {
            doctorProvider: { isNoGapNib },
        } = encounter;

        if (insuranceType == 11)
            return "Charging any fee above Medicare benefits requires charging the total fee. The patient can get a partial refund from Medicare later.";
        else if (fundData && !isNoGapNib) {
            const { maxGap, healthFundName } = fundData;

            if (healthFundName == "NIB" && !isNoGapNib) {
                if (maxGap == 500)
                    return "NIB GapSure expressly restricts charging a gap of over $500 for bills with a surgical base unit value of 5 or greater. Proceeding with this bill means the patient must pay your total fee, and may result in NIB requesting that you refund the excess fees, or removing you from the GapSure scheme.";
                else
                    return "NIB GapSure expressly restricts charging any gap fee for bills with a surgical base unit value of 4 or less. Proceeding with this bill means the patient must pay your total fee, and may result in NIB requesting that you refund the excess fees, or removing you from the GapSure scheme.";
            } 
        } 
        if(!encounter.isAgreementHospitalFund)
            return ""
        return "The maximum allowable gap is exceeded resulting in a lower rebate and the patient must pay the total fee and be refunded later.";
    };

    const _getDisplayItems = (arr, isAsa) => {
        const res = [];
        let sumOfUnit = 0;

        arr.map((item) => {
            res.push(isAsa ? item.itemCode : item.itemNum);
            sumOfUnit += isAsa ? (item.amaUnits ?? 0) : getMBSRebateUnits(item);
        });

        return `${res.join(", ")} ${sumOfUnit ? `(${sumOfUnit} units total)` : ""}`;
    };

    const _getDisplayBillingTypes = (
        isWcOrTacUsingAmaRates = false,
        insuranceType,
        insuranceId,
        timeItemExists = false,
        isAsaItems = false,
        doctorProvider = null
    ) => {
        // Shows "Set Unit Value" as the only option and oopType should ALWAYS be set as 0
        if (isWcOrTacUsingAmaRates || (insuranceType == 18 && isAsaItems)) {
            billingObj.type = 2;
            billingObj.oopType = 0;

            return BillingType.filter((type) => type.id == 2);
        }

        let options = [
            ...BillingType.filter((type) => type.id > 1 && type.id < 6),
        ];

        if(!isAsaItems) {
            // Add the rest of the % rules only for when using MBS items
            options = [
                ...options,
                { id: 6, type: "Set % of MBS Schedule fee" },
                { id: 7, type: "Set % of DVA Schedule fee" },
                { id: 8, type: "Set % of BUPA Schedule fee" },
                { id: 9, type: "Set % of AHSA Schedule fee" },
                { id: 10, type: "Set % of Medibank Schedule fee" },
                { id: 11, type: "Set % of NIB Medigap Schedule fee" },
                { id: 12, type: "Set % of HCF Schedule fee" },
                { id: 13, type: "Set % of AMA Schedule fee" },
            ]
        }
        try {
            if(doctorProvider){
                //chenge label on BUPA and HCF rules
                options.find(o => o.id == 8).type = `Set % of BUPA ${doctorProvider.isNoGapBupa ? "No Gap" : "Known Gap"} Schedule fee`;
                options.find(o => o.id == 12).type = `Set % of HCF ${doctorProvider.isNoGapHcf ? "No Gap" : "Known Gap"} Schedule fee`;
            }
        }
        catch(e){

        }

        // Overseas hide "Set Gap Fee" because insurance rebates are 0
        // Overseas add more billing rules
        if (insuranceType == 10 && !insuranceId)
            options = options.filter((type) => type.id != 3);

        // For Cosmetic ASA allow only set unit value and set total fee
        if (insuranceType == 15 && isAsaItems)
            options = options.filter((type) => type.id == 2 || type.id == 5);

        // Hide "Set Hourly Rate" if no time item selected
        if (!timeItemExists) options = options.filter((type) => type.id != 4);

        // Hide "Set % of MBS Schedule fee" if using ASA items
        if (isAsaItems) options = options.filter((type) => type.id != 6);

       /*  // Hide "Set % of MBS Schedule fee" && "Set % of DVA Schedule fee" if insurance type is NOT Public in Private
        // Add "Set % of MBS Schedule fee" for insurance type Overseas
        if (insuranceType != 30)
            options = options.filter(
                (type) =>
                    (insuranceType == 10 ? true : type.id != 6) && type.id != 7,
            ); */

        return options;
    };

    const _getShowPatientPaysSelector = (obj, fund, isNoGapProgram) => {
        const { insuranceType, billingType } = obj;
        if(isNoGapProgram){
            return false;
        }

        if (isWcOrTacUsingAmaRates && billingType == 2) {
            billingObj.patientPaysTotalFee = false;
            return true;
        }

        const isMedicareBulk =
            insuranceType == 11 && !isObjectEmpty(fund) && fund.gap == 0;
        const isPipEncounter = insuranceType == 30;
        // show patient pays selector for not pip encounters and when not medicare bulk bill;

        const shouldShowPatientPaysSelector =
            !isPipEncounter && !isMedicareBulk;

        return shouldShowPatientPaysSelector;
    };

    const _getShowChargePatientSelector = (
        isAsa,
        isQuote,
        fund,
        isFromQuoteOnlyEncounter = false,
        latestObj,
        isEncounterInThePast,
        billingData,
    ) => {
        // if (isFromQuoteOnlyEncounter) return false;

        if(!isQuote) {
            latestObjDetails.newOopType = Number(1); //force it to after, always on invoice
            return false;
        }

        if (!isQuote && billingData && billingData.length) {
            const latestQuoteObj = billingData.find(
                (data) => data.isLatestQuoteData,
            );

            // if encounter in past and no quote created, set oopType to Post pay and hide options
            if (!latestQuoteObj && isEncounterInThePast) {
                latestObjDetails.newOopType = Number(1);
                return false;
            }
        }

        const { oopType } = latestObj;

        // if encounter scheduled date is in the past and already select charge patient After, hide selector
        if (isEncounterInThePast && oopType == 1) return false;

        const asaValidation = isAsa && isQuote;
        const mbsValidation = !isAsa && !isObjectEmpty(fund) && fund.gap > 0;

        return asaValidation || mbsValidation;
    };

    const _getSaveBtnDisabled = (obj, isGapFeeInvalid) => {
        if (
            !billingObj.type ||
            (isGapFeeInvalid && $user.role != DirectUserRoles.Admin)
        )
            return true;

        if (obj.gap && obj.gap < 0) return true;

        return false;
    };

    const _getMinUnitValue = (arr = []) => {
        if (!arr || !arr.length) return 0;

        let hfSum = 0,
            unitsSum = 0;

        arr.map(({ hfRebate, units }) => {
            hfSum += hfRebate;
            unitsSum += units;
        });

        return parseFloat(hfSum / unitsSum);
    };

    const _initSelectedBillingRule = async () => {
        let {
            billingType,
            calculationAmount,
            rebate,
            actualGap,
            totalFee,
            patientPaysTotalFee,
            oopType,
            usingAmaRates,
            insuranceType,
            withinFundRules,
            patientInsuranceId,
            patientInsuranceType
        } = latestObjDetails;

        // for insuranceType may allow AMA rates option, check and assign
        const billingRuleUseAmaRates =
            (insuranceTypesMayAllowAmaBillingRule.includes(insuranceType) &&
                usingAmaRates) ||
            latestObjDetails.insuranceType == 18;

        let forceBillingType = billingType
            ? billingType
            : billingRuleUseAmaRates
              ? Number(2)
              : "";

        if(insuranceType == DirectInsuranceType.PublicInPrivate && !billingType){
            //is pip and no billing rule selected
            var encounterPipDetails = patientEncounterDetails.patientEncounterPIPData;
            if(encounterPipDetails){
                if(encounterPipDetails.billingType){
                    switch(encounterPipDetails.billingType) {
                        case 2: {
                            //unit value
                            calculationAmount = encounterPipDetails.unitValueAmount;
                            forceBillingType = encounterPipDetails.billingType;
                            break;
                        }
                        case 6: {
                            //mbs
                            calculationAmount = encounterPipDetails.perMbsScheduleAmount;
                            forceBillingType = encounterPipDetails.billingType;
                            break;
                        }
                        case 7: {
                            //dva schedule amount
                            calculationAmount = encounterPipDetails.perDvaScheduleAmount;
                            forceBillingType = encounterPipDetails.billingType;
                            break;
                        }
                        default: break;
                    }
                }
            }
        }

        billingObj = {
            ...billingObj,
            patientInsuranceId: patientInsuranceId,
            type: forceBillingType,
            expectedInsuranceRebate: rebate ?? 0,
            gap: actualGap ?? 0,
            totalFee: totalFee ?? 0,
            patientPaysTotalFee: patientPaysTotalFee,
            patientInsuranceType: patientInsuranceType,
            usingAmaRates,
        };

        // gap amount used to determine whether mbs items are valid or not
        fundsResults = { ...fundsResults, gap: actualGap, withinFundRules };

        showPatientPaysSelector = _getShowPatientPaysSelector(
            latestObjDetails,
            { gap: billingObj.gap },
            patientEncounterDetails.isNoGapProgram
        );

        showChargePatientSelector = _getShowChargePatientSelector(
            isAsaItems,
            isFromQuote,
            { gap: billingObj.gap },
            isFromQuoteOnlyEncounter,
            latestObjDetails,
            isEncounterInThePast,
            patientEncounterDetails,
        );

        if (showChargePatientSelector)
            latestObjDetails.newOopType = oopType ?? Number(0);

        handleAmountSelectorProperty(false);

        billingObj.amount =
            calculationAmount || calculationAmount == 0
                ? calculationAmount
                : billingRuleUseAmaRates
                  ? $amaUnitValue
                  : billingObj.amount
                    ? billingObj.amount
                    : 0;

        isGapFeeInvalid =
            billingObj.type == 3 &&
            billingObj.amount <= amountPatientAlreadyPays &&
            !(billingObj.amount === 0 && amountPatientAlreadyPays === 0);

       /*  if(billingType != null) {
             
            const patientPaysResponse = await patientPaysTotalFeeBasedOnCalculation(
                false,
                insuranceType,
                {
                    gap: billingObj.gap,
                    withinFundRules,
                },
                billingObj,
                patientEncounterDetails
            );
            if(patientPaysResponse != null) {
                const {
                    patientPaysTotalFee,
                    gapFeeBtnDisabled,
                    totalFeeBtnDisabled,
                    disableText: resDisableText,
                } = patientPaysResponse;

                billingObj.patientPaysTotalFee = patientPaysTotalFee;
                isGapFeeBtnDisabled = gapFeeBtnDisabled;

                isTotalFeeBtnDisabled = totalFeeBtnDisabled;

                disableText = resDisableText;
            }
        } */

        hideExpectedInsuranceRebateWarningText =
            fundsResults.withinFundRules ||
            (!fundsResults.withinFundRules &&
                latestObjDetails.patientInsurance == "Basic Plus");
        
        displayExpectedInsuranceRebateWarningText =
            _getInsuranceRebateWarningText(
                latestObjDetails,
                patientEncounterDetails,
                fundData,
            );

        if(!billingObj.type){
            //highlight error on billing type dropdown
            addErrorInput("billingTypeSelector", []);
        }
    };

    const _getAmountPatientAlreadyPays = () => {
        if (amountPatientAlreadyPays) return;

        isGapFeeInvalid = false;
        amountPatientAlreadyPays = 0;

        paymentsData.map((payment) => {
            const { paidBy, status, amount, processingFee } = payment;

            if (paidBy != 0) return;

            if (status == 1 || status == 2)
                amountPatientAlreadyPays += amount - processingFee;

            if (status == 5) amountPatientAlreadyPays -= amount;
        });
    };

    const _checkZeroGapWithBillingRule = () => {
        // check if related billing data support billing rule Set Gap Fee
        if (!displayBillingTypes.some((bt) => bt.id == 3)) return;

        _openUnitValueRuleWithZeroGapModal();
    };

    const _openUnitValueRuleWithZeroGapModal = () =>
        (showUnitValueRuleWithZeroGapModal = true);

    const _closeUnitValueRuleWithZeroGapModal = () =>
        (showUnitValueRuleWithZeroGapModal = false);

    const _openBillingRuleUnitValueAndNoGapProviderModal = () =>
        (billingRuleUnitValueAndNoGapProviderWarningModalOpen = true);

    const _closeBillingRuleUnitValueAndNoGapProviderModal = () =>
        (billingRuleUnitValueAndNoGapProviderWarningModalOpen = false);

    const _privateTotalFeeWithinFundRules = () =>
        latestObjDetails.insuranceType == DirectInsuranceType.Private &&
        fundsResults.withinFundRules &&
        billingObj.patientPaysTotalFee;

    const _openConfirmSavePrivateTotalWithinFundRulesModal = () => {
        confirmSavePrivateTotalWithinFundRules = false;
        showConfirmSavePrivateTotalWithinFundRulesModal = true;
    };

    const _closeConfirmSavePrivateTotalWithinFundRulesModal = () =>
        (showConfirmSavePrivateTotalWithinFundRulesModal = false);

    const _getMaxUnitValue = () => {
        const { insuranceType, useAsaCodes } = latestObjDetails;

        if (insuranceType == DirectInsuranceType.Cosmetic && useAsaCodes)
            return 500;

        if (isWcOrTacOrDefOrCusOrOverAMARates(latestObjDetails))
            return $amaUnitValue * 2;

        return $amaUnitValue;
    };

    const applyQuoteRule = async () => {
        var quoteData = patientEncounterDetails.billingData.find((b) => b.isLatestQuoteData);
        //set the ryle type and amount properties
        billingObj.type = quoteData.billingType;
        await handleAmountSelectorProperty(false); //no need for calculation

        //set the billing obj amount
        billingObj.amount = quoteData.calculationAmount;

        //calculate totals again
        await calculateTotals(false);

        //maintain the selected patient claim from quote
        patientClaim = quoteData.patientClaim;

        //handlePatientPaysTotal(quoteData.patientPaysTotalFee);
    }

    const restoreDefaultInvoiceRule = async () => {
        var quoteData = patientEncounterDetails.billingData.find((b) => b.isLatestQuoteData);
        //set the ryle type and amount properties
        if(quoteData.patientPaysTotalFee){
            //set rule as total fee
            billingObj.type = 5;
            await handleAmountSelectorProperty(false); //no need for calculation
            billingObj.amount = quoteData.totalFee;
        } else {
            //set rule as gap fee
            billingObj.type = 3;
            await handleAmountSelectorProperty(false); //no need for calculation
            billingObj.amount = quoteData.actualGap;
        }

        //calculate totals again
        await calculateTotals(false);

        handlePatientPaysTotal(quoteData.patientPaysTotalFee);
    }

    const onAmountInputFocus = (element) => {
        if(element)
            element.select();

        amountFocused = true;
    };

    const onAmountInputUnFocus = (element) => {
        amountFocused = false;
    };

    const getInsuranceInfo = async () => {
         if (billingObj.patientInsuranceType == DirectInsuranceType.Private) {
            if (!$insuranceFund?.length) await cacheInsuranceFunds();

            if (billingObj.patientInsuranceId) {
                const targetFund = $insuranceFund.find(
                    (fund) => fund.id == billingObj.patientInsuranceId,
                );

                if (targetFund) {
                    return `PRIVATE \nThis patient is covered by ${targetFund.name}.`;
                }
            }
        }
        return "";
    }

</script>

<svelte:window bind:innerHeight />

<NotificationModal
    title={`Select ${isFromQuote ? "Quote": "Invoice"} billing rule`}
    isModalOpen={isAddBillingRuleModalOpen}
    cancelText="Cancel"
    modalMaxWidth="max-w-3xl"
    isConfirming={isUpdatingBillingRule}
    closeModal={() => closeModal()}
    hideActions={true}
    {btnOptions}
    closeModalFunction={closeModal}
    noContentPadding
>
    {#if privateWithInsuranceRebatesZero}
        <div class="error-sum-insurance-zero">
            <div>
                Insurance fund rebates are not configured for this fund. This may be because the provider number is not correctly registered to this fund.
                <br/>
                <br/>
                Please contact support for assistance in resolving this matter.
            </div>
            <div class="flex flex-row gap-x-4 justify-center pt-4">
                <Button
                    manualClass={`btn btn-sm btn-secondary btn-error`}
                    clickFunc={async () => closeModal()}
                >
                    {"Cancel"}
                </Button>
               
            </div>
        </div>
    {:else}
        <div class="flex flex-col flex-1 overflow-hidden gap-y-4 p-6">
            <p class="leading-6 text-sm">Codes: {displayItems}</p>

            <div class="divider mt-0 mb-0">Rule Details</div>

            <div
                class="flex flex-1 flex-col p-1 gap-y-4 overflow-y-auto overflow-x-hidden {hasOverflow
                    ? 'pr-[10px]'
                    : ''}"
                bind:this={contentRef}
            >
                <div class="flex flex-1">
                    <select
                        class="select select-sm select-bordered flex-1"
                        on:change={handleSelectBillingRule}
                        disabled={isEncounterInNoGapProgram}
                        bind:value={selectedBillingRuleId}
                    >
                        <option value="" disabled selected>
                            Select billing rule to use as template
                        </option>
                        {#each filteredDoctorBillingRules as brTemplate}
                            <option value={brTemplate.id}>{brTemplate.name}</option>
                        {/each}
                    </select>
                </div>

                <div class="flex flex-1" >
                    <select
                        id="billingTypeSelector"
                        class="select select-sm select-bordered flex-1"
                        bind:value={billingObj.type}
                        on:change={handleAmountSelectorProperty}
                        disabled={isEncounterInNoGapProgram}
                    >
                        <option value="" disabled> Select billing type </option>
                        {#each displayBillingTypes as type}
                            <option value={type.id}>{type.type}</option>
                        {/each}
                    </select>
                </div>
                {#if latestObjDetails.insuranceType == DirectInsuranceType.PublicInPrivate}
                    <div class="text-center font-bold text-sm">
                        Please enter the agreed value for the public-in-private agreement with this hospital
                    </div>
                {/if}

                <div class="flex flex-1 items-center justify-between">
                    <p class="leading-6 text-sm">
                        {_getDisplayBillingObjTitle(billingObj.type)}
                    </p>

                    <div class="flex items-center gap-x-2">
                        <button
                            class="btn btn-secondary btn-sm text-[#fff]"
                            on:click|preventDefault={handleSelectPrev}
                            disabled={isEncounterInNoGapProgram}
                        >
                            <i class="fa faIcon">&#xf104;</i>
                        </button>

                        <DecimalInput
                            inputClass="w-36"
                            bind:value={billingObj.amount}
                            onFocus={onAmountInputFocus}
                            onBlur={onAmountInputUnFocus}
                            debounceTime={100}
                            inputFunc={(ev) => {
                                if(billingObj.amount < amountSelectorProperties.min) {
                                    billingObj.amount = amountSelectorProperties.min;
                                    calculateTotals(false)
                                } else {
                                    //all good, calculate totals
                                    calculateTotals(false)
                                }
                            }}
                            isDisabled={isEncounterInNoGapProgram}
                        />

                        <button
                            class="btn btn-secondary btn-sm text-[#fff]"
                            on:click|preventDefault={handleSelectNext}
                            disabled={isEncounterInNoGapProgram}
                        >
                            <i class="fa faIcon">&#xf105;</i>
                        </button>
                    </div>
                </div>

                {#if billingObj.type}
                    <div class="flex flex-1">
                        <input
                            type="range"
                            class="range range-secondary {isEncounterInNoGapProgram
                                ? 'cursor-not-allowed'
                                : 'cursor-pointer'}"
                            min={dynamicMin}
                            max={Number(amountSelectorProperties.max)}
                            disabled={isEncounterInNoGapProgram}
                            step={dynamicStep}
                            bind:value={billingObj.amount}
                            on:change={() => calculateTotals(true)}
                            on:input={() => {
                                dynamicMin =
                                    billingObj.amount >
                                    Math.ceil(Number(amountSelectorProperties.min))
                                        ? Math.ceil(
                                            Number(amountSelectorProperties.min),
                                        )
                                        : Number(amountSelectorProperties.min);

                                if (amountSelectorProperties.max - billingObj.amount <
                                    amountSelectorProperties.divisionsDivider)
                                    dynamicStep =
                                        amountSelectorProperties.divisionsDivider / 10;
                                else
                                    dynamicStep =
                                        amountSelectorProperties.divisionsDivider;

                                if (billingObj.amount - dynamicMin <
                                    amountSelectorProperties.divisionsDivider)
                                    billingObj.amount = Number(
                                        amountSelectorProperties.min,
                                    );
                            }}
                        />
                    </div>
                    {#if isEncounterInNoGapProgram}
                        <div class="flex w-full items-center gap-x-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                class="stroke-secondary h-6 w-6"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <p class="leading-6 text-sm text-secondary pt-1">
                                Encounter is included in the 'No Gap Program'. Rule was set as $0 gap fee and cannot be modified.
                            </p>
                        </div>
                    {/if}

                    <div class="flex flex-col flex-1 gap-y-2">
                        {#if encounterTypeAnaesthesia}
                            <div class="flex flex-1 gap-x-2">
                                <div class="flex flex-1 flex-col">
                                <p class="leading-6 text-sm">Unit value</p>
                                <div class="flex items-center gap-x-2">
                                    {#if isCalculatingTotals}
                                        <Loader />
                                    {/if}
                                    <DecimalInput
                                        inputClass="w-full"
                                        isDisabled={true}
                                        bind:value={unitValue}
                                    />
                                </div>
                                </div>
                                <div class="flex flex-1 flex-col">
                                    <p class="leading-6 text-sm">Hourly rate</p>
                                    <div class="flex items-center gap-x-2">
                                        {#if isCalculatingTotals}
                                            <Loader />
                                        {/if}
                                        {#if hourlyRate}
                                            <DecimalInput
                                                inputClass="w-full"
                                                isDisabled={true}
                                                bind:value={hourlyRate}
                                            />
                                        {:else}
                                            <input
                                                type="text"
                                                placeholder="No Time Added"
                                                class="input input-sm w-full"
                                                disabled
                                            />
                                        {/if}
                                    </div>
                                </div>
                            </div>
                        {/if}
                        <div class="flex flex-1 gap-x-2">
                            <div class="flex flex-1 flex-col">
                                <p class="leading-6 text-sm">
                                    {#if billingObj.patientInsuranceType == DirectInsuranceType.Private}
                                        <InfoIcon info={insuranceInfoDetails} />
                                    {/if}
                                    Expected insurance rebate
                                </p>
                                <div class="flex items-center gap-x-2">
                                    {#if isCalculatingTotals}
                                        <Loader />
                                    {/if}
                                    <div
                                        class={`${billingObj.type && !hideExpectedInsuranceRebateWarningText && displayExpectedInsuranceRebateWarningText ? "border border-2 rounded-md border-red-600 w-full" : "w-full"}`}
                                    >
                                        <DecimalInput
                                            inputClass="w-full"
                                            isDisabled={true}
                                            bind:value={billingObj.expectedInsuranceRebate}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="flex flex-1 flex-col">
                                <p class="leading-6 text-sm">Gap</p>
                                <div class="flex items-center gap-x-2">
                                    {#if isCalculatingTotals}
                                        <Loader />
                                    {/if}
                                    <DecimalInput
                                        inputClass="w-full"
                                        isDisabled={true}
                                        bind:value={billingObj.gap}
                                    />
                                </div>
                            </div>
                            <div class="flex flex-1 flex-col">
                                <p class="leading-6 text-sm">Total Fee</p>
                                <div class="flex items-center gap-x-2">
                                    {#if isCalculatingTotals}
                                        <Loader />
                                    {/if}
                                    <DecimalInput
                                        inputClass="w-full"
                                        isDisabled={true}
                                        bind:value={billingObj.totalFee}
                                    />
                                </div>
                            </div>
                        </div>

                        {#if !hideExpectedInsuranceRebateWarningText && displayExpectedInsuranceRebateWarningText}
                            <div class="flex flex-1">
                                <p class="leading-6 text-sm text-error">
                                    {displayExpectedInsuranceRebateWarningText}
                                </p>
                            </div>
                        {/if}
                    </div>

                    <div class="flex flex-col flex-1 gap-y-2">
                        <!-- Patient pays options -->
                        <div class="flex flex-1 items-center justify-between">
                            <p class="leading-6 text-sm">Patient pays:</p>
                            <div class="flex items-center gap-x-4">
                                <button
                                    class={`btn btn-sm rounded rounded-full text-xs ${
                                        !billingObj.hasOwnProperty(
                                            "patientPaysTotalFee",
                                        )
                                            ? "btn-secondary"
                                            : billingObj.patientPaysTotalFee
                                            ? "btn-secondary"
                                            : "btn-primary"
                                    } ${showPatientPaysSelector && !isGapFeeBtnDisabled ? "" : "cursor-not-allowed btn-active btn-ghost"} ${isGapFeeBtnDisabled ? "tooltip tooltip-secondary" : ""}`}
                                    data-tip={disableText}
                                    on:click|preventDefault={() =>
                                        handlePatientPaysTotal(false)}
                                >
                                    {!isObjectEmpty(fundsResults) &&
                                    (fundsResults?.gap == 0 || fundsResults.maxGap == 0)
                                        ? "No Gap"
                                        : "Gap Fee"}
                                </button>
                                <button
                                    class={`btn btn-sm rounded rounded-full text-xs ${
                                        !billingObj.hasOwnProperty(
                                            "patientPaysTotalFee",
                                        )
                                            ? "btn-secondary"
                                            : billingObj.patientPaysTotalFee
                                            ? "btn-primary"
                                            : "btn-secondary"
                                    } ${showPatientPaysSelector && !isTotalFeeBtnDisabled ? "" : "cursor-not-allowed btn-active btn-ghost"} ${isTotalFeeBtnDisabled ? "tooltip tooltip-secondary" : ""}`}
                                    data-tip={disableText}
                                    on:click|preventDefault={() =>
                                        handlePatientPaysTotal(true)}
                                >
                                    Total Fee
                                </button>
                            </div>
                        </div>

                        <!-- Patient claim options -->
                        {#if showPatientClaimToggle && billingObj.patientPaysTotalFee}
                            <div class="flex flex-1 items-center justify-between">
                                <p class="leading-6 text-sm">
                                    Patient claim:
                                    <CustomTooltip contentStyle="bottom: 0px !important; top: auto !important;" zIndex={5}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            class="stroke-secondary h-6 w-6 cursor-helper"
                                            slot="tooltipTrigger"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>

                                        <div class="flex flex-col gap-y-1">
                                            <p>Patient Claim Interactive (PCI) allows you to submit a claim on behalf of the patient, so the rebate from a total fee invoice is paid directly to the patient.</p>
                                        </div>
                                    </CustomTooltip>
                                </p>
                                {#if billingObj.patientInsuranceId && $noPatientClaimFunds.includes(billingObj.patientInsuranceId)}
                                    <div  class="leading-6 text-sm">Not permitted for this fund</div>
                                {:else}
                                    <div class="flex items-center gap-x-4">
                                        <button
                                            class={`btn btn-sm rounded rounded-full text-xs ${
                                                patientClaim
                                                    ? "btn-primary"
                                                    : "btn-secondary"
                                            } ${billingObj.patientPaysTotalFee ? "" : "cursor-not-allowed btn-active btn-ghost"} ${!billingObj.patientPaysTotalFee ? "tooltip tooltip-secondary" : ""}`}
                                            data-tip={patientClaimDisabledText}
                                            on:click|preventDefault={() => { patientClaim = true; }}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            class={`btn btn-sm rounded rounded-full text-xs ${
                                                patientClaim
                                                    ? "btn-secondary"
                                                    : "btn-primary"
                                            }`}
                                            data-tip={patientClaimDisabledText}
                                            on:click|preventDefault={() => { patientClaim = false; }}
                                        >
                                            No
                                        </button>
                                    </div>
                                {/if}
                            </div>
                        {/if}

                        <!-- Charge patient options -->
                        {#if showChargePatientSelector}
                            <div class="flex flex-1 items-center justify-between">
                                <p class="leading-6 text-sm">Charge patient:</p>
                                <div class="flex items-center gap-x-4">
                                    <button
                                        class={`btn btn-sm rounded rounded-full text-xs ${
                                            !latestObjDetails.hasOwnProperty("newOopType")
                                                ? "btn-secondary"
                                                : latestObjDetails.newOopType == 0
                                                ? "btn-primary"
                                                : "btn-secondary"
                                        } ${showChargePatientSelector ? "" : "cursor-not-allowed btn-active btn-ghost"}`}
                                        on:click|preventDefault={() =>
                                            (latestObjDetails.newOopType = 0)}
                                    >
                                        Before
                                    </button>
                                    <button
                                        class={`btn btn-sm rounded rounded-full text-xs ${
                                            !latestObjDetails.hasOwnProperty("newOopType")
                                                ? "btn-secondary"
                                                : latestObjDetails.newOopType == 1
                                                ? "btn-primary"
                                                : "btn-secondary"
                                        } ${showChargePatientSelector ? "" : "cursor-not-allowed btn-active btn-ghost"}`}
                                        on:click|preventDefault={() =>
                                            (latestObjDetails.newOopType = 1)}
                                    >
                                        After
                                    </button>
                                </div>
                            </div>
                        {/if}

                        <!-- {#if isGapFeeInvalid}
                            <p class="leading-6 text-error">
                                {amountPatientAlreadyPays == latestObjDetails.totalFee
                                    ? "The total fee"
                                    : "A gap"} of {getAmountInCurrency(
                                    amountPatientAlreadyPays,
                                )}
                                has already been paid for this encounter, please adjust the
                                gap fee accordingly.
                            </p>
                        {/if} -->
                    </div>
                {/if}
                
                {#if invoiceReasonDetails != null}
                    <InvoiceReasonDetails 
                        encounterDetails={patientEncounterDetails}
                        reason={invoiceReasonDetails?.invoiceReason} 
                        applyQuoteRule={applyQuoteRule}
                        restoreDefaultInvoiceRule={restoreDefaultInvoiceRule}
                        difference={invoiceReasonDetails?.patpDifferenceAbs}></InvoiceReasonDetails>
                {/if}
                <div class="flex flex-row gap-x-4 justify-between pt-4">
                    <div>
                        {#if !isFromQuote}
                            <InvoiceReasonDetails 
                                mode={1}
                                bind:billingObj={billingObj}
                                encounterDetails={patientEncounterDetails}
                                reason={invoiceReasonDetails?.invoiceReason} 
                                applyQuoteRule={applyQuoteRule}
                                restoreDefaultInvoiceRule={restoreDefaultInvoiceRule}
                                difference={invoiceReasonDetails?.patpDifferenceAbs}>
                            </InvoiceReasonDetails>
                        {/if}
                    </div>
                    <div class="flex-1 flex flex-row gap-x-4 justify-end">
                        
                        <Button
                            manualClass={`btn btn-sm btn-secondary btn-error`}
                            clickFunc={async () => closeModal()}
                        >
                            {"Cancel"}
                        </Button>
                        <Button
                            manualClass={`btn btn-sm btn-primary ${isUpdatingBillingRule ? "cursor-not-allowed" : ""}`}
                            clickFunc={async () => await handleSaveBillingRule()}
                            isDisabled={isSaveBtnDisabled || isUpdatingBillingRule || isCalculatingTotals || amountFocused}
                            disabledTooltip={amountFocused ? "Unfocus the amount selection" : "Calculation in progress"}
                        >
                            {isUpdatingBillingRule ? "Saving" : "Save"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    {/if}
</NotificationModal>

<NotificationModal
    isModalOpen={billingRuleUnitValueAndNoGapProviderWarningModalOpen}
    title="Notice"
    cancelText="Cancel"
    closeModal={_closeBillingRuleUnitValueAndNoGapProviderModal}
    submitText="Proceed"
    confirmAction={handleSelectBillingRuleUnitValueAndNoGapProvider}
    closeModalFunction={_closeBillingRuleUnitValueAndNoGapProviderModal}
>
    <div class="flex flex-col flex-1 gap-y-4">
        <p>
            Setting a unit value as a no gap provider will result in a total fee
            being charged.
        </p>
        <p>Do you wish to proceed?</p>
        <p>
            Consider using Gap fee = 0, if you do not wish a total fee to be
            charged.
        </p>

    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={showUnitValueRuleWithZeroGapModal}
    title="Zero Gap"
    cancelText="Cancel"
    submitText={isCalculatingTotals ? "Confirming" : "Confirm"}
    closeModal={_closeUnitValueRuleWithZeroGapModal}
    confirmAction={updateUnitValueRuleToSetGapFee}
    isConfirming={isCalculatingTotals}
    closeModalFunction={_closeUnitValueRuleWithZeroGapModal}
>
    <p>Your billing rule results in known gap = 0.</p>
    <p>Please confirm you would like to proceed with this.</p>
</NotificationModal>

<NotificationModal
    title="Confirmation needed"
    isModalOpen={showConfirmSavePrivateTotalWithinFundRulesModal}
    cancelText="Cancel"
    closeModal={_closeConfirmSavePrivateTotalWithinFundRulesModal}
    closeModalFunction={_closeConfirmSavePrivateTotalWithinFundRulesModal}
    submitText="Acknowledge"
    confirmAction={async () => {
        confirmSavePrivateTotalWithinFundRules = true;
        _closeConfirmSavePrivateTotalWithinFundRulesModal();
        await updateBillingRule();
    }}
>
    <p class="leading-6 p-4">
        Warning: You have selected to send the FULL account to the patient for
        payment. In this scenario, some health funds will only rebate the
        Medicare schedule fee. The calculation above assumes the health fund
        will rebate their usual above-schedule fees. Please check with the
        specific health fund for clarification.
    </p>
</NotificationModal>

<style>
    .tooltip::before {
        transform: translateX(-80%);
    }

    .error-sum-insurance-zero {
        color: red;
        font-weight: bold;
        padding: 10px;
        text-align: center;
        height: 200px;
        display: flex;
        flex-direction: column;
        gap:20px;
        align-items: center;
        justify-content: center;
        margin: 20px;
        background: #fdf5f5;
        border-radius: 10px;
    }
</style>
